import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useRestaurants } from "../../hooks/useRestaurants";
import { useStoreOffers } from "../../hooks/useOffers";
import { Restaurant } from "../../types/restaurant";
import { TimeSelectWheel } from "../../components/TimeSelectWheel";
import { DateSelectWheel } from "../../components/DateSelectWheel";
import { PeopleSelectWheel } from "../../components/PeopleSelectWheel";
import { ArrowLeft, Calendar, ChevronDown, Navigation, Phone, Sparkles, Utensils, Bath, Zap, ParkingCircle, Droplets, Dot, BookAudio } from "lucide-react";
import { useTableBooking } from "../../hooks/useTableBooking";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/button";
import { format } from "date-fns";
import { Drawer, DrawerContent } from "../../components/ui/drawer";
import { TimeSelectWheelRestricted } from "../../components/TimeSelectWheelRestricted";
import RestImageCarousel from "../main-page/components/rest-image-carousel/RestImageCarousel";
import AnimatedLoader from "../../components/AnimatedLoader";
import RestReviews from "./components/rest-reviews/RestReviews";
import OtherRestaurants from "./components/other-restaurants/OtherRestaurants";
import Rating from "../../components/rating/Rating";
import { calculateSingleDistance } from "../../utils/firebaseFunctionsDistance";


const AMENITY_CONFIG = [
  { id: 'food', icon: <Utensils size={16} />, label: 'Fresh Food', text: 'Farm-fresh organic ingredients' },
  { id: 'washroom', icon: <Bath size={16} />, label: 'Sanitary', text: 'Touchless premium restrooms' },
  { id: 'ev', icon: <Zap size={16} />, label: 'EV Station', text: 'Fast universal EV charging' },
  { id: 'parking', icon: <ParkingCircle size={16} />, label: 'Parking', text: 'Secure valet-ready parking' },
  { id: 'windshield', icon: <Droplets size={16} />, label: 'Auto Care', text: 'Windshield cleaning support' },
];

const PrebookingRestaurent = () => {
  const { restId } = useParams<{ restId: string }>();
  const navigate = useNavigate();
  const { data: restaurants = [], isLoading } = useRestaurants();
  const { data: storeOffers = [] } = useStoreOffers(restId || null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);

  const [time, setTime] = useState("");
  const [isTimeDrawerOpen, setIsTimeDrawerOpen] = useState(false);

  // Table booking states
  const [isBookingDrawerOpen, setIsBookingDrawerOpen] = useState(false);
  const [numberOfPeople, setNumberOfPeople] = useState(1);
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isBookingDateDrawerOpen, setIsBookingDateDrawerOpen] = useState(false);
  const [isBookingTimeDrawerOpen, setIsBookingTimeDrawerOpen] = useState(false);
  const [isPeopleDrawerOpen, setIsPeopleDrawerOpen] = useState(false);
  const [isBookingSuccess, setIsBookingSuccess] = useState(false);

  // Check if there's navigation history
  const [canGoBack, setCanGoBack] = useState(false);
  const [activeFacility, setActiveFacility] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [distanceText, setDistanceText] = useState<string | null>(null);

  const { mutate: createBooking, isPending } = useTableBooking();
  // const { data: storeOffers = [], isLoading: isLoadingOffers } = useStoreOffers(storeID);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check navigation history on mount
  useEffect(() => {
    // Check if there's a previous entry in the history stack
    const hasHistory = window.history.length > 1;
    setCanGoBack(hasHistory);
  }, []);

  // Find restaurant by ID
  useEffect(() => {
    if (restaurants.length > 0 && restId) {
      const foundRestaurant = restaurants.find((r) => r.dbID === restId);
      setRestaurant(foundRestaurant || null);
    }
  }, [restaurants, restId]);

  // Request user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.log("Location access denied in Prebooking:", error);
          setUserLocation(null);
        }
      );
    }
  }, []);

  // Calculate distance when location and restaurant are available
  useEffect(() => {
    const getDistance = async () => {
      if (userLocation && restaurant && restaurant.storeLat && restaurant.storeLong) {
        try {
          const origin = { lat: userLocation.lat, lng: userLocation.lng };
          const destination = {
            lat: parseFloat(restaurant.storeLat as any),
            lng: parseFloat(restaurant.storeLong as any),
          };
          const result = (await calculateSingleDistance(origin, destination)) as any;
          if (result && result.distanceText) {
            setDistanceText(result.distanceText);
          }
        } catch (error) {
          console.error("Error calculating single distance:", error);
          setDistanceText(null);
        }
      }
    };
    getDistance();
  }, [userLocation, restaurant]);

  const handleViewStore = () => {
    if (restaurant) {
      navigate(
        `/restaurent?storeID=${restaurant.dbID}&storeType=${restaurant.storeType}`
      );
    }
  };

  const handleNavigate = () => {
    if (restaurant?.dbID) {
      navigate(`/restaurant-map?storeID=${restaurant.dbID}`);
    } else {
      toast.error("Location not available");
    }
  };

  const handleContact = () => {
    const phone = restaurant?.restaurantNo || restaurant?.restaurantOwnerNo;
    if (phone) {
      window.open(`tel:${phone}`);
    } else {
      toast.error("Phone number not available");
    }
  };

  const handleContinue = () => {
    if (!restaurant) return;

    if (!time) {
      toast.error("Please select a time");
      return;
    }

    // Navigate to restaurant page with time
    navigate(
      `/restaurent?storeID=${restaurant.dbID}&storeType=${restaurant.storeType}&time=${time}`
    );
  };

  const handleBookTable = () => {
    // Validate inputs
    if (!numberOfPeople || numberOfPeople < 1) {
      toast.error("Please select number of people");
      return;
    }

    if (!bookingDate) {
      toast.error("Please select a date");
      return;
    }

    if (!bookingTime) {
      toast.error("Please select a time");
      return;
    }

    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    // Create booking
    createBooking(
      {
        restaurantId: restaurant!.dbID,
        restaurantName: restaurant!.name,
        numberOfPeople: numberOfPeople,
        bookingDate: format(new Date(bookingDate), "yyyy-MM-dd"),
        bookingTime: bookingTime,
        phoneNumber: phoneNumber,
      },
      {
        onSuccess: (bookingId) => {
          toast.success("Table reserved successfully!");
          console.log("Booking created with ID:", bookingId);
          // Reset form
          setNumberOfPeople(1);
          setBookingDate("");
          setBookingTime("");
          setPhoneNumber("");
          setIsBookingSuccess(true);
          // setIsBookingDrawerOpen(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to reserve table");
          console.error("Booking error:", error);
        },
      }
    );
  };


  console.log("restaurant", restaurant, storeOffers);

  if (isLoading || !restaurant) {
    return <AnimatedLoader />;
  }

  // if (!restaurant) {
  //   return (
  //     <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
  //       <div className="text-center">
  //         <h2 className="text-white text-xl mb-4">Restaurant not found</h2>
  //         <Button
  //           onClick={() => navigate("/")}
  //           className="bg-[#6366f1] hover:bg-[#5558e3]"
  //         >
  //           Go Back
  //         </Button>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen pb-6">
      <RestImageCarousel
        key={restaurant.dbID}
        restID={restaurant.dbID}
        restaurantName={restaurant.name}
        priceForTwo={restaurant.restDiscount || "₹1,500 for two"}
      />

      <div className="pt-2">
        <div className="px-4 pb-5" id="overview">
          {/* Restaurant name and rating box */}
          <div className="flex justify-between items-start mb-1">
            <div className="flex flex-col ">
              <h1 className="text-2xl font-bold text-white leading-tight pr-4">
                {restaurant.name}
              </h1>

              <div className="text-gray-400 text-sm mb-4 flex items-center flex-wrap gap-x-2 gap-y-1">
                <span className={`${restaurant.status === 'Active' ? 'text-green-500' : 'text-red-500'} font-medium flex items-center -ml-1`}>
                  <Dot className="mr-[-4px]" />
                  {restaurant.status === 'Active' ? 'Open now' : 'Closed'}
                </span>
                <span className="text-gray-500">•</span>
                <span className="text-gray-400 font-medium">{restaurant.storeType || "Restaurant"}</span>
                {restaurant.pureVeg && (
                  <>
                    <span className="text-gray-500">•</span>
                    <span className="text-emerald-500 font-semibold text-[10px] border border-emerald-500/30 px-1.5 py-0.5 rounded bg-emerald-500/5">Veg</span>
                  </>
                )}
                <span className="text-gray-500">•</span>
                <span className="text-gray-500">
                  {restaurant.timings || (restaurant.openingTime && restaurant.closingTime2 ? `${restaurant.openingTime} - ${restaurant.closingTime2}` : "24 Hours")}
                </span>
              </div>
            </div>
            <Rating
              rating={restaurant.restRating}
              totalRatings={restaurant.totalRatings || "100+"}
            />
          </div>


          <div className="text-gray-400 text-sm mb-1 flex items-center gap-1">
            <span>{restaurant.area || "Navi Mumbai"}</span>
            {restaurant.city && <span>, {restaurant.city}</span>}
            <button className="text-gray-500"><ChevronDown size={14} /></button>
          </div>

          {distanceText && (
            <div className="text-gray-400 text-sm mb-1">
              <span className="font-medium">{distanceText}</span>
            </div>
          )}

          {/* Action pill buttons */}
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar my-5 px-1">
            <button
              onClick={handleViewStore}
              className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] hover:border-white/20 hover:bg-white/[0.06] px-5 py-3 rounded-[16px] whitespace-nowrap transition-all duration-300 group"
            >

              <span className="text-gray-300 text-[12px] font-bold tracking-tight">View Menu</span>
            </button>
            {restaurant.isTableBooking && (
              <button
                onClick={() => setIsBookingDrawerOpen(true)}
                className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] hover:border-white/20 hover:bg-white/[0.06] px-5 py-3 rounded-[16px] whitespace-nowrap transition-all duration-300 group"
              >
                <Sparkles size={14} className="text-purple-400/80 group-hover:scale-110 transition-transform" />
                <span className="text-gray-300 text-[12px] font-bold tracking-tight">Book a table</span>
              </button>
            )}
            {restaurant.isPrebooking && (
              <button
                onClick={() => {
                  const el = document.getElementById('prebooking');
                  if (el) {
                    const yOffset = -80;
                    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }
                }}
                className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] hover:border-white/20 hover:bg-white/[0.06] px-5 py-3 rounded-[16px] whitespace-nowrap transition-all duration-300 group"
              >
                <span className="text-gray-300 text-[12px] font-bold tracking-tight">Prebook Order</span>
              </button>
            )}
            <button
              onClick={handleNavigate}
              className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] hover:border-white/20 hover:bg-white/[0.06] px-5 py-3 rounded-[16px] whitespace-nowrap transition-all duration-300 group"
            >
              <Navigation size={14} className="text-gray-500 group-hover:text-blue-400 transition-colors" />
              <span className="text-gray-300 text-[12px] font-bold tracking-tight">Navigate</span>
            </button>
            <button
              onClick={handleContact}
              className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] hover:border-white/20 hover:bg-white/[0.06] px-5 py-3 rounded-[16px] whitespace-nowrap transition-all duration-300 group"
            >
              <Phone size={14} className="text-gray-500 group-hover:text-emerald-400 transition-colors" />
              <span className="text-gray-300 text-[12px] font-bold tracking-tight">Contact</span>
            </button>
          </div>


          {/* Facilities Icons Section */}
          <div className="mt-6 mb-4 px-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white/90 text-sm font-bold uppercase tracking-[0.2em]">Amenities</h2>
              <div className="flex items-center gap-1.5 py-1 px-2.5 bg-white/[0.03] border border-white/[0.05] rounded-full">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                <span className="text-gray-500 text-[9px] font-bold uppercase tracking-wider">Verified Venue</span>
              </div>
            </div>

            <div className="flex gap-4 overflow-x-auto no-scrollbar py-1">
              {(restaurant.amenities && restaurant.amenities.length > 0
                ? AMENITY_CONFIG.filter(amenity => restaurant.amenities?.includes(amenity.id))
                : AMENITY_CONFIG
              ).map((fac) => {
                const isActive = activeFacility === fac.text;
                return (
                  <button
                    key={fac.id}
                    onClick={() => setActiveFacility(isActive ? null : fac.text)}
                    className="flex flex-col items-center flex-shrink-0 group outline-none"
                  >
                    <div className={`
                      w-12 h-12 rounded-[16px] flex items-center justify-center mb-1.5 transition-all duration-500
                      ${isActive
                        ? 'bg-white text-black shadow-[0_10px_20px_-5px_rgba(255,255,255,0.15)] scale-105'
                        : 'bg-white/[0.03] border border-white/[0.06] text-gray-500 hover:text-gray-300 hover:border-white/10 hover:bg-white/[0.05]'
                      }
                    `}>
                      {fac.icon}
                    </div>
                    <span className={`text-[9px] font-bold tracking-tight transition-colors duration-300 ${isActive ? 'text-white' : 'text-gray-600'}`}>
                      {fac.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {activeFacility && (
              <div className="mt-4 animate-in fade-in slide-in-from-top-1 duration-500 ease-out">
                <div className="bg-white/[0.02] border border-white/[0.05] backdrop-blur-md rounded-[20px] p-4 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-[10px] bg-white/[0.05] flex items-center justify-center border border-white/[0.05]">
                    <Sparkles size={12} className="text-indigo-400/80" />
                  </div>
                  <p className="text-gray-400 text-[11px] font-medium leading-relaxed tracking-tight">
                    {activeFacility}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Pre-Booking Section */}
          {restaurant.isPrebooking && <div className="my-8">
            <div id="prebooking" className="flex justify-between items-end mb-6 px-1">
              <div className="flex flex-col gap-1.5">
                {/* <h2 className="text-white text-xs font-black uppercase tracking-[0.2em] opacity-30">Selection</h2> */}
                <p className="font-bold text-lg text-white/90">Prebook Order</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
                <Calendar size={14} className="text-white/40" />
              </div>
            </div>

            <div className="bg-white/[0.02] border border-white/[0.06] rounded-[24px] p-5 mb-8 backdrop-blur-md">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/10">
                  <Sparkles size={14} className="text-indigo-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white/90 text-[11px] font-bold uppercase tracking-tight mb-1">Reservation Policy</p>
                  <p className="text-gray-500 text-[12px] leading-relaxed">
                    Advance bookings require a 60-minute preparation window. Please curate your arrival time accordingly.
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}

            <div className="grid grid-cols-1 gap-5">
              <div className="group">
                <label
                  htmlFor="time"
                  className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/20 mb-3 ml-1"
                >
                  Arrival Window
                </label>
                <div
                  onClick={() => setIsTimeDrawerOpen(true)}
                  className="w-full bg-white/[0.03] text-white px-6 py-4 rounded-[22px] border border-white/[0.08] group-hover:bg-white/[0.05] group-hover:border-white/20 transition-all duration-300 flex justify-between items-center cursor-pointer shadow-sm active:scale-[0.99]"
                >
                  <span className={`text-sm font-bold ${time ? "text-white" : "text-white/20"}`}>
                    {time || "Select optimal time"}
                  </span>
                  <ChevronDown size={18} className="text-white/20 group-hover:text-white/40 transition-colors" />
                </div>
              </div>

              <Button
                onClick={handleContinue}
                className="w-full bg-white text-black hover:bg-white/90 font-black text-[12px] tracking-[0.1em] py-5 rounded-[22px] transition-all duration-500 shadow-xl shadow-white/5 active:scale-[0.98] uppercase"
              >
                Prebook Order
              </Button>
            </div>
          </div>}



          {/* Tabs Section */}
          <div className="sticky top-0 z-40 -mx-4 px-4 backdrop-blur-xl border-b border-white/[0.06] mb-8">
            <div className="flex gap-7 overflow-x-auto no-scrollbar py-0.5">
              {[
                { label: "Overview", id: "overview" },
                // { label: "Reviews", id: "reviews" },
                { label: "About", id: "about" },
                { label: "Similar", id: "similar" }
              ].map((tab, idx) => {
                const isActive = idx === 0;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      const el = document.getElementById(tab.id);
                      if (el) {
                        const yOffset = -80;
                        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                      }
                    }}
                    className={`py-4 text-[11px] font-black uppercase tracking-[0.15em] whitespace-nowrap transition-all relative ${isActive ? "text-white" : "text-gray-600 hover:text-gray-400"
                      }`}
                  >
                    {tab.label}
                    {isActive && (
                      <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white shadow-[0_0_10px_rgba(255,255,255,0.3)]" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Address Section */}
          {restaurant.address && (
            <div className="mb-4">
              <p className="text-gray-300 text-sm leading-relaxed">
                {restaurant.address}
                {restaurant.area && `, ${restaurant.area}`}
                {restaurant.city && `, ${restaurant.city}`}
                {restaurant.pincode && ` - ${restaurant.pincode}`}
              </p>
            </div>
          )}

          {/* Reviews Section */}
          {/* <div id="reviews">
            <RestReviews
              rating={restaurant.restRating}
              totalRatings={restaurant.totalRatings || "100+"}
            />
          </div> */}


          {/* About Section */}
          <div className="mb-4" id="about">
            <h3 className="text-lg font-semibold text-white mb-2">About</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {restaurant.about ||
                `Welcome to ${restaurant.name}! We offer a delightful dining experience with a wide variety of cuisines. Our menu features carefully crafted dishes made with fresh ingredients. Whether you're looking for a quick bite or a leisurely meal, we're here to serve you with quality food and excellent service.`}
            </p>
          </div>


          {/* Floating Action Bar */}
          <div className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-6 pointer-events-none">
            <div className="max-w-md mx-auto pointer-events-auto rounded-[28px] overflow-hidden border border-[#442c75] bg-[#140e1d] shadow-[0_20px_50px_rgba(0,0,0,0.8)]">
              {/* Cashback Banner */}
              {storeOffers.length > 0 && (
                <div
                  onClick={() => navigate(`/paybill?storeID=${restaurant.dbID}&storeType=${restaurant.storeType}`)}
                  className="bg-gradient-to-r from-[#2d1b54] via-[#3d2373] to-[#2d1b54] px-3 py-2 flex justify-between items-center cursor-pointer"
                >
                  <p className="text-[#e9d5ff] text-[13px] font-medium">{storeOffers[0].title}</p>
                  <ChevronDown size={16} className="text-[#e9d5ff] -rotate-90" />
                </div>
              )}

              {/* Action Buttons */}
              <div className="p-3.5 flex gap-2.5 bg-white/5">
                {restaurant.isTableBooking ? (
                  <button
                    onClick={() => setIsBookingDrawerOpen(true)}
                    className="flex-1 py-2 text-white border border-white/20 bg-transparent hover:bg-white/5 rounded-full font-medium text-[12px] sm:text-[13px] transition-all duration-300 truncate px-1"
                  >
                    Book a table
                  </button>
                ) : (<button
                  onClick={() => navigate(`/restaurent?storeID=${restaurant.dbID}&storeType=${restaurant.storeType}`)}
                  className="flex-1 py-3 text-white border border-white/20  hover:bg-white/5 rounded-full font-bold text-sm transition-all duration-300 truncate px-1 border-white bg-white/10"
                >
                  Catalogue
                </button>)}

                <button
                  onClick={() => navigate(`/paybill?storeID=${restaurant.dbID}&storeType=${restaurant.storeType}`)}
                  className="flex-1  bg-white text-black hover:bg-gray-100 rounded-full text-sm font-bold transition-all duration-300 truncate px-1"
                >
                  Pay bill
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
      {/* Other Restaurants Section */}
      <div id="similar">
        <OtherRestaurants restaurants={restaurants} currentRestId={restaurant.dbID} />
      </div>

      {/* Time Picker Drawer */}
      <Drawer open={isTimeDrawerOpen} onOpenChange={setIsTimeDrawerOpen}>
        <DrawerContent className="bg-[#1a1a1a] border-t border-gray-800">
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-4">Select Time</h3>
            <div className="flex justify-center">
              <TimeSelectWheelRestricted onChange={setTime} />
            </div>
            <Button
              onClick={() => setIsTimeDrawerOpen(false)}
              className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white font-semibold py-4 rounded-xl transition-colors mt-6"
            >
              Confirm
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Table Booking Drawer */}
      <Drawer open={isBookingDrawerOpen} onOpenChange={(open) => {
        setIsBookingDrawerOpen(open);
        if (!open) setIsBookingSuccess(false);
      }}>
        <DrawerContent
          className="max-h-[90vh] bg-[#1a1a1a] border-t border-gray-800"
          aria-label="Table booking form"
        >
          <div className="overflow-y-auto p-6">
            {isBookingSuccess ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mb-6 border border-emerald-500/20">
                  <Sparkles size={40} className="text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h2>
                <p className="text-gray-400 mb-8">
                  Your table at <span className="text-white font-medium">{restaurant?.name}</span> has been successfully reserved.
                </p>
                <Button
                  onClick={() => setIsBookingDrawerOpen(false)}
                  className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white font-semibold py-4 rounded-xl transition-colors"
                >
                  Great!
                </Button>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-white mb-6">
                  Reserve a Table
                </h2>

                {/* Form */}
                <div className="space-y-4 mb-6">
                  {/* Date and Time Row */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Date */}
                    <div>
                      <label
                        htmlFor="bookingDate"
                        className="block text-gray-400 text-sm mb-2"
                      >
                        Date
                      </label>
                      <input
                        type="text"
                        readOnly
                        placeholder="Select Date"
                        value={bookingDate}
                        onClick={() => setIsBookingDateDrawerOpen(true)}
                        className="w-full bg-black text-white px-4 py-3 rounded-xl border border-gray-800 focus:border-[#6366f1] focus:outline-none transition-colors placeholder:text-gray-600 cursor-pointer"
                      />
                    </div>
                    {/* Time */}
                    <div>
                      <label
                        htmlFor="bookingTime"
                        className="block text-gray-400 text-sm mb-2"
                      >
                        Time
                      </label>
                      <input
                        type="text"
                        readOnly
                        placeholder="Select Time"
                        value={bookingTime}
                        onClick={() => setIsBookingTimeDrawerOpen(true)}
                        className="w-full bg-black text-white px-4 py-3 rounded-xl border border-gray-800 focus:border-[#6366f1] focus:outline-none transition-colors placeholder:text-gray-600 cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Number of People and Phone Number Row */}
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    {/* Number of People */}
                    <div>
                      <label
                        htmlFor="numberOfPeople"
                        className="block text-gray-400 text-sm mb-2"
                      >
                        Attendees
                      </label>
                      <input
                        type="text"
                        readOnly
                        placeholder="Select People"
                        value={
                          numberOfPeople
                            ? `${numberOfPeople} ${numberOfPeople === 1 ? "person" : "people"
                            }`
                            : ""
                        }
                        onClick={() => setIsPeopleDrawerOpen(true)}
                        className="w-full bg-black text-white px-4 py-3 rounded-xl border border-gray-800 focus:border-[#6366f1] focus:outline-none transition-colors placeholder:text-gray-600 cursor-pointer"
                        aria-label="Number of people for reservation"
                      />
                    </div>
                    {/* Phone Number */}
                    <div>
                      <label
                        htmlFor="phoneNumber"
                        className="block text-gray-400 text-sm mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        id="phoneNumber"
                        type="tel"
                        placeholder="Phone Number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="w-full bg-black text-white px-4 py-3 rounded-xl border border-gray-800 focus:border-[#6366f1] focus:outline-none transition-colors placeholder:text-gray-600"
                        aria-label="Phone number for reservation"
                      />
                    </div>
                  </div>

                  {/* Note about larger groups */}
                  <div className="bg-[#2a2a2a] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400 text-[10px] leading-relaxed">
                      <span className="text-gray-300 font-medium">Note:</span> For
                      groups larger than 10 people, please book one more table or
                      contact the restaurant directly.
                    </p>
                  </div>
                </div>

                {/* Action Button */}
                <Button
                  onClick={handleBookTable}
                  disabled={isPending}
                  className="w-full bg-[#6366f1] hover:bg-[#5558e3] disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl transition-colors"
                  aria-label={`Reserve table at ${restaurant?.name}`}
                >
                  {isPending ? "Reserving..." : "Reserve Table"}
                </Button>
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Booking Date Picker Drawer */}
      <Drawer
        open={isBookingDateDrawerOpen}
        onOpenChange={setIsBookingDateDrawerOpen}
      >
        <DrawerContent className="bg-[#1a1a1a] border-t border-gray-800 min-h-[40vh]">
          <div className="pl-4">
            <h3 className="text-xl font-bold text-white mb-4">Select Date</h3>
            <div className="flex justify-center">
              <DateSelectWheel onChange={setBookingDate} />
            </div>
            <div className="pr-4 pb-4">
              <Button
                onClick={() => setIsBookingDateDrawerOpen(false)}
                className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white font-semibold py-4 rounded-xl transition-colors mt-6"
              >
                Confirm
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Booking Time Picker Drawer */}
      <Drawer
        open={isBookingTimeDrawerOpen}
        onOpenChange={setIsBookingTimeDrawerOpen}
      >
        <DrawerContent className="bg-[#1a1a1a] border-t border-gray-800">
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-4">Select Time</h3>
            <div className="flex justify-center">
              <TimeSelectWheel onChange={setBookingTime} />
            </div>
            <Button
              onClick={() => setIsBookingTimeDrawerOpen(false)}
              className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white font-semibold py-4 rounded-xl transition-colors mt-6"
            >
              Confirm
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* People Picker Drawer */}
      <Drawer open={isPeopleDrawerOpen} onOpenChange={setIsPeopleDrawerOpen}>
        <DrawerContent className="bg-[#1a1a1a] border-t border-gray-800 min-h-[40vh]">
          <div className="pl-4 pt-4">
            <h3 className="text-xl font-bold text-white mb-4">
              Select Number of People
            </h3>
            <div className="flex justify-center">
              <PeopleSelectWheel onChange={setNumberOfPeople} />
            </div>
            <div className="pr-4 pb-4">
              <Button
                onClick={() => setIsPeopleDrawerOpen(false)}
                className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white font-semibold py-4 rounded-xl transition-colors mt-6"
              >
                Confirm
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div >
  );
};

export default PrebookingRestaurent;
